import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createConnection, updateConnection, validateConnection } from '../services/api';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faDatabase,
  faFileLines,
  faTable,
  faXmark,
  faCircleCheck,
  faCircleExclamation,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

const TYPE_OPTIONS = [
  { id: 'postgres', label: 'PostgreSQL', icon: faDatabase, desc: 'Relational database' },
  { id: 'mysql', label: 'MySQL / MariaDB', icon: faDatabase, desc: 'WordPress & MySQL databases' },
  { id: 'oracle', label: 'Oracle', icon: faDatabase, desc: 'Enterprise database' },
  { id: 'sap_hana', label: 'SAP HANA', icon: faDatabase, desc: 'In-memory analytics' },
  { id: 'csv', label: 'CSV', icon: faFileLines, desc: 'Upload CSV files' },
  { id: 'excel', label: 'Excel', icon: faTable, desc: 'Upload Excel spreadsheets' },
];

const UPLOAD_TYPES = new Set(['csv', 'excel']);

const DEFAULT_FORM = {
  name: '',
  host: '',
  port: '',
  database: '',
  service_name: '',
  username: '',
  password: '',
  ssl: false,
};

const DEFAULT_PORTS = {
  postgres: 5432,
  mysql: 3306,
  oracle: 1521,
  sap_hana: 30015,
};

const FIELD_SETS = {
  postgres: ['host', 'port', 'database', 'username', 'password', 'ssl'],
  mysql: ['host', 'port', 'database', 'username', 'password'],
  oracle: ['host', 'port', 'service_name', 'username', 'password'],
  sap_hana: ['host', 'port', 'username', 'password'],
};

function getTypeLabel(type) {
  return TYPE_OPTIONS.find(t => t.id === type)?.label || 'Connection';
}

function buildConfig(type, form) {
  if (type === 'postgres') {
    return {
      host: form.host.trim(),
      port: Number(form.port),
      database: form.database.trim(),
      username: form.username.trim(),
      password: form.password,
      ssl: !!form.ssl,
    };
  }
  if (type === 'mysql') {
    return {
      host: form.host.trim(),
      port: Number(form.port),
      database: form.database.trim(),
      username: form.username.trim(),
      password: form.password,
    };
  }
  if (type === 'oracle') {
    return {
      host: form.host.trim(),
      port: Number(form.port),
      service_name: form.service_name.trim(),
      username: form.username.trim(),
      password: form.password,
    };
  }
  if (type === 'sap_hana') {
    return {
      host: form.host.trim(),
      port: Number(form.port),
      username: form.username.trim(),
      password: form.password,
    };
  }
  return {};
}

function getInitialForm(connection) {
  if (!connection) return { ...DEFAULT_FORM };
  const config = connection.config || {};
  return {
    name: connection.name || '',
    host: config.host || '',
    port: config.port != null ? String(config.port) : '',
    database: config.database || '',
    service_name: config.service_name || '',
    username: config.username || '',
    password: '',
    ssl: !!config.ssl,
  };
}

function validateForm(type, form) {
  const errors = {};
  const fields = FIELD_SETS[type] || [];
  fields.forEach(field => {
    if (field === 'ssl') return;
    const value = `${form[field] ?? ''}`.trim();
    if (!value) errors[field] = 'Required';
  });

  const port = Number(form.port);
  if (fields.includes('port') && (!Number.isInteger(port) || port <= 0)) {
    errors.port = 'Enter a positive port number';
  }
  return errors;
}

// Database connections only — CSV/Excel now go through the dedicated
// /connect/:type page instead of this modal.
export default function ConnectModal({ onClose, onCreated, onUpdated, initialConnection = null }) {
  const isEdit = !!initialConnection;
  const navigate = useNavigate();
  const createdConnectionIdRef = useRef(initialConnection?.id || null);
  const [step, setStep] = useState(1);
  const [type, setType] = useState(initialConnection?.type || null);
  const [form, setForm] = useState(() => getInitialForm(initialConnection));
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testState, setTestState] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialConnection) {
      setType(initialConnection.type);
      setForm(getInitialForm(initialConnection));
      setStep(2);
    }
  }, [initialConnection]);

  const connectionTypes = useMemo(() => TYPE_OPTIONS, []);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: undefined }));
    setTestState(null);
  };

  const resetIncompatibleFields = (nextType) => {
    const next = { ...DEFAULT_FORM, name: form.name };
    if (nextType === 'postgres') next.port = String(DEFAULT_PORTS.postgres);
    if (nextType === 'mysql') next.port = String(DEFAULT_PORTS.mysql);
    if (nextType === 'oracle') next.port = String(DEFAULT_PORTS.oracle);
    if (nextType === 'sap_hana') next.port = String(DEFAULT_PORTS.sap_hana);
    setForm(prev => ({
      ...next,
      name: prev.name,
      host: '',
      database: '',
      service_name: '',
      username: '',
      password: '',
      ssl: false,
    }));
    setErrors({});
    setTestState(null);
  };

  const handleTypeSelect = (nextType) => {
    if (UPLOAD_TYPES.has(nextType)) {
      onClose();
      navigate(`/connect/${nextType}`);
      return;
    }
    setType(nextType);
    setStep(2);
    resetIncompatibleFields(nextType);
  };

  const validateAndSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Enter a connection name');

    const formErrors = validateForm(type, form);
    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      return toast.error('Please fix the highlighted fields');
    }

    setLoading(true);
    try {
      let conn;
      const payload = {
        name: form.name.trim(),
        type,
        config: buildConfig(type, form),
      };
      if (isEdit || createdConnectionIdRef.current) {
        conn = await updateConnection(createdConnectionIdRef.current || initialConnection.id, payload);
      } else {
        conn = await createConnection(payload);
        createdConnectionIdRef.current = conn.id;
      }

      setTestState({ status: 'loading', message: 'Testing connection…' });
      try {
        const validation = await validateConnection(conn.id);
        if (validation?.valid === false || validation?.status === 'error') {
          const message = validation?.message || validation?.detail || 'Connection test failed';
          setTestState({ status: 'error', message });
          throw new Error(message);
        }
        setTestState({ status: 'success', message: validation?.message || 'Connection successful' });
      } catch (validationErr) {
        const message = validationErr?.message || 'Connection test failed';
        setTestState({ status: 'error', message });
        throw validationErr;
      }

      toast.success(`"${conn.name}" connected!`);
      if (isEdit) onUpdated?.(conn);
      else onCreated?.(conn);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to connect');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    const formErrors = validateForm(type, form);
    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    if (isEdit && !initialConnection?.id) return;

    setTesting(true);
    setTestState({ status: 'loading', message: 'Testing connection…' });
    try {
      const payload = {
        name: form.name.trim(),
        type,
        config: buildConfig(type, form),
      };
      let preview;
      if (createdConnectionIdRef.current || isEdit) {
        const conn = await updateConnection(createdConnectionIdRef.current || initialConnection.id, payload);
        createdConnectionIdRef.current = conn.id;
        preview = await validateConnection(conn.id);
      } else {
        const conn = await createConnection(payload);
        createdConnectionIdRef.current = conn.id;
        preview = await validateConnection(conn.id);
      }
      if (preview?.valid === false || preview?.status === 'error') {
        const message = preview?.message || preview?.detail || 'Connection test failed';
        setTestState({ status: 'error', message });
        toast.error(message);
        return;
      }
      setTestState({ status: 'success', message: preview?.message || 'Connection successful' });
      toast.success('Connection successful');
    } catch (err) {
      const message = err.message || 'Connection test failed';
      setTestState({ status: 'error', message });
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const fields = FIELD_SETS[type] || [];

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <div>
            <h2 style={s.title}>{step === 1 ? 'New Connection' : `${isEdit ? 'Edit' : 'Connect'} ${getTypeLabel(type)}`}</h2>
            <p style={s.subtitle}>{step === 1 ? 'Choose your data source type' : 'Configure your connection details'}</p>
          </div>
          <button onClick={onClose} style={s.closeBtn} aria-label="Close dialog">
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        </div>

        {step === 1 ? (
          <div style={s.typeGrid} className="connect-type-grid">
            {connectionTypes.map(t => (
              <button
                key={t.id}
                onClick={() => handleTypeSelect(t.id)}
                style={{
                  ...s.typeCard,
                  ...(type === t.id ? s.typeCardActive : {}),
                }}
              >
                <span style={s.typeCardTop}>
                  <span style={s.typeIcon}><FontAwesomeIcon icon={t.icon} aria-hidden="true" /></span>
                  {type === t.id && <span style={s.activeCheck}><FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" /></span>}
                </span>
                <strong style={s.typeLabel}>{t.label}</strong>
                <span style={s.typeDesc}>{t.desc}</span>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={validateAndSubmit} style={s.form}>
            <section style={s.sectionCard}>
              <div style={s.sectionHeader}>
                <h3 style={s.sectionTitle}>Connection Information</h3>
                <p style={s.sectionSub}>Enter the credentials needed to establish the connection.</p>
              </div>

              <div style={s.formGrid} className="connect-form-grid">
                <div style={s.field}>
                  <label style={s.label}>Connection Name</label>
                  <input
                    style={s.input}
                    placeholder="e.g. Production DB"
                    value={form.name}
                    onChange={e => set('name', e.target.value)}
                    autoFocus
                  />
                </div>

                {fields.includes('host') && (
                  <div style={s.field}>
                    <label style={s.label}>{type === 'sap_hana' ? 'SAP Host' : 'Host'}</label>
                    <input style={s.input} value={form.host} onChange={e => set('host', e.target.value)} />
                    {errors.host && <span style={s.error}>{errors.host}</span>}
                  </div>
                )}

                {fields.includes('port') && (
                  <div style={s.field}>
                    <label style={s.label}>Port</label>
                    <input
                      style={s.input}
                      type="number"
                      min="1"
                      step="1"
                      value={form.port}
                      onChange={e => set('port', e.target.value)}
                    />
                    {errors.port && <span style={s.error}>{errors.port}</span>}
                  </div>
                )}

                {fields.includes('database') && (
                  <div style={s.field}>
                    <label style={s.label}>Database</label>
                    <input style={s.input} value={form.database} onChange={e => set('database', e.target.value)} />
                    {errors.database && <span style={s.error}>{errors.database}</span>}
                  </div>
                )}

                {fields.includes('service_name') && (
                  <div style={s.field}>
                    <label style={s.label}>Service Name</label>
                    <input style={s.input} value={form.service_name} onChange={e => set('service_name', e.target.value)} />
                    {errors.service_name && <span style={s.error}>{errors.service_name}</span>}
                  </div>
                )}

                {fields.includes('username') && (
                  <div style={s.field}>
                    <label style={s.label}>Username</label>
                    <input style={s.input} value={form.username} onChange={e => set('username', e.target.value)} spellCheck={false} />
                    {errors.username && <span style={s.error}>{errors.username}</span>}
                  </div>
                )}

                {fields.includes('password') && (
                  <div style={s.field}>
                    <label style={s.label}>Password</label>
                    <input
                      style={s.input}
                      type="password"
                      value={form.password}
                      onChange={e => set('password', e.target.value)}
                      autoComplete="new-password"
                    />
                    {errors.password && <span style={s.error}>{errors.password}</span>}
                  </div>
                )}
              </div>
            </section>

            {fields.includes('ssl') && (
              <section style={s.sectionCard}>
                <div style={s.sectionHeader}>
                  <h3 style={s.sectionTitle}>Advanced Settings</h3>
                  <p style={s.sectionSub}>Optional connection options.</p>
                </div>
                <label style={s.switchRow}>
                  <input
                    type="checkbox"
                    checked={form.ssl}
                    onChange={e => set('ssl', e.target.checked)}
                  />
                  <span>
                    <strong style={s.switchLabel}>Use SSL</strong>
                    <span style={s.switchSub}>Encrypt traffic between the app and database.</span>
                  </span>
                </label>
              </section>
            )}

            {testState && (
              <div
                style={{
                  ...s.statusBox,
                  ...(testState.status === 'success' ? s.statusSuccess : {}),
                  ...(testState.status === 'error' ? s.statusError : {}),
                }}
              >
                <FontAwesomeIcon
                  icon={testState.status === 'loading' ? faSpinner : testState.status === 'success' ? faCircleCheck : faCircleExclamation}
                  spin={testState.status === 'loading'}
                />
                <span>{testState.message}</span>
              </div>
            )}

            <div style={s.actions}>
              {!isEdit && (
                <button type="button" onClick={() => setStep(1)} style={s.backBtn}>
                  <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" /> Back
                </button>
              )}
              <button
                type="button"
                onClick={testConnection}
                style={s.testBtn}
                disabled={testing || loading}
              >
                {testing ? <><FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" /> Testing...</> : 'Test Connection'}
              </button>
              <button type="submit" style={{ ...s.connectBtn, ...(loading ? { opacity: 0.7 } : {}) }} disabled={loading}>
                {loading ? <><span style={s.miniSpinner} /> {isEdit ? 'Saving...' : 'Connecting...'}</> : `${isEdit ? 'Save' : 'Connect'} ${getTypeLabel(type)}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    animation: 'fadeIn 0.2s ease',
  },
  modal: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)',
    borderRadius: 24, padding: 28, width: 'min(96vw, 980px)', maxWidth: 980,
    boxShadow: 'var(--shadow-lg)', animation: 'scaleIn 0.25s ease',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16 },
  title: { fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6, letterSpacing: '-0.02em' },
  subtitle: { fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 },
  closeBtn: {
    width: 32, height: 32, borderRadius: 8, background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)',
    cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 14,
    marginBottom: 8,
  },
  typeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 156,
    padding: 18,
    borderRadius: 18,
    border: '1.5px solid var(--border-subtle)',
    background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-tertiary) 100%)',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  typeCardActive: {
    borderColor: 'var(--accent)',
    background: 'linear-gradient(180deg, var(--accent-subtle) 0%, var(--bg-primary) 100%)',
    boxShadow: '0 10px 30px rgba(59, 130, 246, 0.12)',
  },
  typeCardTop: { display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' },
  typeIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-subtle)',
    color: 'var(--accent)',
    fontSize: 18,
    flexShrink: 0,
  },
  activeCheck: {
    width: 20,
    height: 20,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--accent)',
    fontSize: 14,
  },
  typeLabel: { fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2 },
  typeDesc: { fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionCard: {
    border: '1px solid var(--border-subtle)',
    borderRadius: 18,
    background: 'var(--bg-primary)',
    padding: 18,
  },
  sectionHeader: { marginBottom: 16 },
  sectionTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4, letterSpacing: '-0.01em' },
  sectionSub: { fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 14,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.6px' },
  input: {
    height: 44,
    padding: '0 14px',
    borderRadius: 12,
    border: '1.5px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'var(--font-main)',
    transition: 'border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease',
  },
  error: { fontSize: 11, color: 'var(--error)', fontFamily: 'var(--font-main)' },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 2,
  },
  switchLabel: { display: 'block', fontSize: 13, color: 'var(--text-primary)', marginBottom: 2, fontWeight: 700 },
  switchSub: { display: 'block', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 },
  statusBox: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 12,
    border: '1px solid var(--border-subtle)', background: 'var(--bg-primary)', fontSize: 13, color: 'var(--text-secondary)',
  },
  statusSuccess: { borderColor: 'rgba(34,197,94,0.35)', color: 'var(--success)' },
  statusError: { borderColor: 'rgba(239,68,68,0.35)', color: 'var(--error)' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4, flexWrap: 'wrap' },
  backBtn: {
    padding: '10px 18px', borderRadius: 10, border: '1.5px solid var(--border-subtle)',
    background: 'none', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer',
  },
  testBtn: {
    minWidth: 150,
    padding: '10px 18px', borderRadius: 12, border: '1.5px solid var(--border-subtle)',
    background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  connectBtn: {
    minWidth: 170,
    padding: '11px 18px', borderRadius: 12, background: 'var(--accent)',
    color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s',
    boxShadow: '0 10px 24px rgba(59, 130, 246, 0.2)',
  },
  miniSpinner: {
    width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
};
